import React from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { useTheme } from '@mui/material';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import PropTypes from "prop-types";
import { useRef } from 'react';

const colorPairs = [
    ["#f00084", "#833ab4"],
    ["#01708f", "#63b5e8"],
    ["#ffcc00", "#ff9933"],
    ["#43b36b", "#1a883f"],
    ["#f58231", "#e03a00"],
    ["#911eb4", "#52057b"],
];
ChartJS.register(ArcElement, Tooltip, Legend);

const DonutChart = ({ dataValues, labels, title }) => {
    const chartRef = useRef(null);
    const theme = useTheme();


    // Function to create a gradient color
    const createGradient = (ctx, chartArea, colorStart, colorEnd) => {
        const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
        gradient.addColorStop(0, colorStart);
        gradient.addColorStop(1, colorEnd);
        return gradient;
    };

    const data = {
        labels: labels,
        datasets: [
            {
                data: dataValues,
                backgroundColor: (context) => {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;

                    if (!chartArea) {
                        return null;
                    }

                    // Create gradients for each segment
                    return dataValues.map((_, index) => {
                        const [colorStart, colorEnd] = colorPairs[index % colorPairs.length];
                        return createGradient(ctx, chartArea, colorStart, colorEnd);
                    });
                },
                borderWidth: 1,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "70%",
        plugins: {
            legend: {
                display: false, // Custom legend below
            },
            tooltip: {
                callbacks: {
                    label: function (tooltipItem) {
                        return `${tooltipItem.label}: ${tooltipItem.raw}`;
                    },
                },
            },
        },
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                height: "100%",
                position: "relative",
                padding: {
                    xs: "1rem",
                    sm: "1.5rem",
                    md: "2rem",
                    lg: "2.5rem"
                },
            }}
        >
            <Typography
                variant="h6"
                component="h6"
                sx={{
                    position: "absolute",
                    top: "1rem",
                    left: {
                        xs: "1rem",
                        sm: "1.5rem",
                        md: "2rem"
                    },
                    right: {
                        xs: "1rem",
                        sm: "1.5rem",
                        md: "2rem"
                    },
                }}
            >
                {title}
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    mt: 5,
                    width: "100%"
                }}
            >
                <Box
                    sx={{
                        position: "relative",
                        width: { xs: "100%", sm: "40%" },
                        height: { xs: "180px", sm: "200px", md: "220px" },
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center"
                    }}
                >
                    <Doughnut ref={chartRef} data={data} options={options} />

                    <Box
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            textAlign: "center",
                        }}
                    >
                        <Typography variant="h5" fontWeight="bold">{dataValues.reduce((sum, val) => sum + val, 0)}</Typography>
                        <Typography variant="caption" color="textSecondary">
                            Total
                        </Typography>
                    </Box>
                </Box>

                <Box
                    sx={{
                        width: { xs: "100%", sm: "60%" },
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {data.labels.map((label, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: "0.625rem",
                                justifyContent: "space-between"
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Box
                                    sx={{
                                        width: "10px",
                                        height: "10px",
                                        backgroundColor: colorPairs[index % colorPairs.length][0],
                                        borderRadius: "50%",
                                        marginRight: "0.5rem"
                                    }}
                                />
                                <Typography variant="body2" fontWeight="bold">
                                    {label}
                                </Typography>
                            </Box>
                            <Typography variant="body2">
                                {dataValues[index]}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

DonutChart.propTypes = {
    title: PropTypes.string,
    dataValues: PropTypes.arrayOf(PropTypes.number),
    labels: PropTypes.arrayOf(PropTypes.string),

}
export default DonutChart;
